"use client";

import { useEffect } from "react";
import { useTranslations } from "next-intl";

import { CtaButton } from "@/components/CtaButton";
import { FramedLink } from "@/components/FramedLink";
import { StatusScreen } from "@/components/StatusScreen";
import { toEyebrow } from "@/lib/eyebrow";
import { reportError } from "@/lib/report-error";

/**
 * Public route error boundary (500). Catches render/runtime errors in the
 * locale tree and renders inside the locale layout (Header/Footer/i18n).
 * Errors in the layout itself escape to `global-error.tsx`.
 */
/* `retry`, not `reset`. A 500 in production is almost always a SERVER
 * component throwing — a query that failed, a fetch that timed out — and
 * `reset()` only clears the error state and re-renders the tree the client
 * already has. There is no fresh payload to render, so the same failure comes
 * straight back and the button reads as doing nothing. `retry()` re-fetches
 * the segment first. Stable since Next 16.3. */
export default function Error({
  error,
  retry,
}: {
  error: Error & { digest?: string };
  retry: () => void;
}) {
  const t = useTranslations("errors.serverError");

  useEffect(() => {
    reportError(error, { digest: error.digest, boundary: "public" });
  }, [error]);

  return (
    <StatusScreen
      code="500"
      eyebrow={toEyebrow(t("eyebrow"))}
      title={t("title")}
      body={t("body")}
      actions={
        <>
          <CtaButton onClick={() => retry()}>{t("retry")}</CtaButton>
          <FramedLink href="/">⮐ {t("home")}</FramedLink>
        </>
      }
    />
  );
}
